import IndigoApi from 'communication/IndigoApi';
import { UIConfiguration } from './uiConfiguration';
import RequestProgressService from 'requestProgress/RequestProgressService';
import {
    buildLogger,
    Logger,
    LoggingService,
} from '@instinet-analytics/core-lib';
import {
    mapFromUiConfigDto,
    mapToUiConfigDto,
    mapToUIConfigFromState,
} from './layoutRequestMapper';
import { ApiRequestOptions } from 'communication/ApiCallInfo';
import UIConfigurationDto from 'communication/types/uiConfiguration/UIConfigurationDto';
import ManagedLayout from 'layoutManagement/ManagedLayout';
import { Layout } from 'layout/LayoutState';
import { initialAppSettingsState } from 'app/store/appSettings/AppSettingsState';
import { initialWidgetsSharedConfigState } from 'widgets/store/sharedConfiguration/WidgetsSharedConfigState';
import { UserPreferencesService } from 'userPreferences/UserPreferencesService';
import { WellKnownLayoutOrigin } from 'layout/WellKnownLayoutOrigin';

const StandaloneLastLoadedLayoutKey = 'StandaloneLastLoadedLayout';
type StandaloneLastLoadedLayoutType = { layoutName: string };

interface LayoutServiceOptions {
    indigoApi: IndigoApi;
    requestProgressService: RequestProgressService;
    loggingService: LoggingService;
    userPreferencesService: UserPreferencesService;
}

export default class LayoutService {
    private indigoApi: IndigoApi;
    private requestProgressService: RequestProgressService;
    private logger: Logger;
    private userPreferencesService: UserPreferencesService;

    public constructor(options: LayoutServiceOptions) {
        this.indigoApi = options.indigoApi;
        this.requestProgressService = options.requestProgressService;
        this.logger = buildLogger('LayoutService', options.loggingService);
        this.userPreferencesService = options.userPreferencesService;
    }

    public async loadLayout(
        layoutName: string,
        origin: string,
        panelId?: string
    ): Promise<UIConfiguration> {
        const apiCall = panelId
            ? (options: ApiRequestOptions) =>
                  this.indigoApi.panelConfigurationLoad(
                      layoutName,
                      origin,
                      panelId,
                      options
                  )
            : (options: ApiRequestOptions) =>
                  this.indigoApi.loadUIConfiguration(
                      layoutName,
                      origin,
                      false,
                      options
                  );

        const uiConfigDto =
            await this.requestProgressService.invokeAndMonitorRequest(
                panelId ? 'panelConfigurationLoad' : 'loadLayout',
                apiCall
            );

        const uiConfig = mapFromUiConfigDto(uiConfigDto);
        this.logger.debug('Loaded layout', {
            layoutName,
            origin,
            panelId,
            uiConfig,
        });
        return uiConfig;
    }

    public async saveLayout(
        uiConfig: UIConfiguration
    ): Promise<UIConfiguration> {
        const uiConfigDto = mapToUiConfigDto(uiConfig);
        const response = await this.saveUiConfigDto(uiConfigDto);
        return mapFromUiConfigDto(response);
    }

    public async saveUiConfigDto(
        uiConfigDto: UIConfigurationDto
    ): Promise<UIConfigurationDto> {
        const apiCall = (options: ApiRequestOptions) =>
            this.indigoApi.saveUIConfiguration(uiConfigDto, options);
        this.logger.debug('Saving layout', uiConfigDto);
        return await this.requestProgressService.invokeAndMonitorRequest(
            'saveLayout',
            apiCall
        );
    }

    public async switchLayout(
        layoutName: string,
        origin: string,
        panelId?: string
    ): Promise<UIConfiguration> {
        const apiCall = panelId
            ? (options: ApiRequestOptions) =>
                  this.indigoApi.panelConfigurationSwitch(
                      layoutName,
                      origin,
                      panelId,
                      options
                  )
            : (options: ApiRequestOptions) =>
                  this.indigoApi.loadUIConfiguration(
                      layoutName,
                      origin,
                      false,
                      options
                  );

        const uiConfigDto =
            await this.requestProgressService.invokeAndMonitorRequest(
                panelId ? 'panelConfigurationSwitch' : 'switchLayout',
                apiCall
            );

        const uiConfig = mapFromUiConfigDto(uiConfigDto);
        this.logger.debug('Switched layout', { layoutName, origin, uiConfig });

        if (origin === WellKnownLayoutOrigin.STANDALONE) {
            this.rememberLastLoadedLayout(layoutName);
        }
        return uiConfig;
    }

    public async fetchLayout(
        layoutName: string,
        origin: string,
        globalVersion: boolean
    ): Promise<UIConfigurationDto> {
        const apiCall = (options: ApiRequestOptions) =>
            this.indigoApi.loadUIConfiguration(
                layoutName,
                origin,
                globalVersion,
                options
            );

        return await this.requestProgressService.invokeAndMonitorRequest(
            'fetchLayout',
            apiCall
        );
    }

    public getAllLayoutsWithSharesForOrigin: (
        origin: string
    ) => Promise<ManagedLayout[]> = async (origin) => {
        const apiCall = (options: ApiRequestOptions) =>
            this.indigoApi.getAllLayoutsWithSharesForUserAndOrigin(
                origin,
                true,
                options
            );

        const layouts =
            await this.requestProgressService.invokeAndMonitorRequest(
                'getAllLayoutsWithSharesForUserAndOrigin',
                apiCall
            );

        return layouts.map((l) => ({
            layoutId: l.id,
            name: l.layoutName,
            layout: l.layout,
            owner: l.username,
            sharedWith: l.usersSharedWith ? l.usersSharedWith.split(',') : [],
            isGlobal: l.global,
        }));
    };

    public shareLayoutWithUser: (
        layoutId: number,
        username: string
    ) => Promise<void> = async (layoutId, username) => {
        const layoutShare = {
            layoutSharingId: layoutId,
            usernameReceiving: username,
        };
        const apiCall = (options: ApiRequestOptions) =>
            this.indigoApi.createNewLayoutShare(layoutShare, options);

        return await this.requestProgressService.invokeAndMonitorRequest(
            'shareLayoutWithUser',
            apiCall
        );
    };

    public duplicateLayout: (
        layoutId: number,
        newLayoutName: string
    ) => Promise<UIConfigurationDto> = async (layoutId, newLayoutName) => {
        const apiCall = (options: ApiRequestOptions) =>
            this.indigoApi.duplicateLayout(layoutId, newLayoutName, options);

        return await this.requestProgressService.invokeAndMonitorRequest(
            'duplicateLayout',
            apiCall
        );
    };

    public deleteLayout = async (layoutId: number) => {
        const apiCall = (options: ApiRequestOptions) =>
            this.indigoApi.deleteUIConfiguration(layoutId, options);

        return await this.requestProgressService.invokeAndMonitorRequest(
            'deleteLayout',
            apiCall
        );
    };

    public createLayout = async (name: string, origin: string) => {
        const layout: Layout = {
            id: null,
            name,
            origin,
            shape: { type: 'row', content: [] },
            components: [],
            activeInputId: undefined,
        };

        const uiConfig = mapToUIConfigFromState(
            layout,
            initialAppSettingsState,
            {},
            initialWidgetsSharedConfigState
        );
        const uiConfigDto = mapToUiConfigDto(uiConfig);

        await this.saveUiConfigDto(uiConfigDto);
    };

    public rememberLastLoadedLayout(layoutName: string) {
        const dto: StandaloneLastLoadedLayoutType = { layoutName };
        this.userPreferencesService
            .saveUserPreference(StandaloneLastLoadedLayoutKey, dto)
            .then(() => {
                this.logger.debug('Remembered last loaded layout', layoutName);
            })
            .catch((err) => {
                this.logger.critical(
                    'Error remembering last loaded layout ' + layoutName,
                    err
                );
            });
    }

    public async getLastLoadedLayout() {
        const dto =
            await this.userPreferencesService.fetchUserPreference<StandaloneLastLoadedLayoutType>(
                StandaloneLastLoadedLayoutKey
            );

        this.logger.debug('Loaded last layout', dto);

        return dto?.layoutName ?? 'default';
    }
}
